/**
 * BRXSession
 *
 * Global BraveRX session service for:
 * - selection state
 * - promo code state
 * - persisted checkout context
 * - resolved outbound checkout URLs
 *
 * Data source: #brx-session-data bootstrap payload (rendered by theme.liquid)
 */

(async function () {
    const STORAGE_KEY = 'brx-session';
    const SESSION_VERSION = 3;

    // --- Bootstrap and parsing ---

    function safeJsonParse(value, fallback = null) {
        if (!value) return fallback;

        try {
            return JSON.parse(value);
        } catch (error) {
            console.warn('[BRXSession] Failed to parse JSON.', error);
            return fallback;
        }
    }

    function getUiConfig() {
        const dataElement = document.getElementById('brx-session-data');

        if (!dataElement) {
            console.warn('[BRXSession] Missing #brx-session-data element.');
            return null;
        }

        return safeJsonParse(dataElement.textContent);
    }

    async function fetchCatalogData(apiBaseUrl) {
        try {
            const res = await fetch(`${apiBaseUrl}/v2/catalog`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            return json?.data || null;
        } catch (err) {
            console.warn('[BRXSession] Catalog fetch failed — continuing with empty catalog.', err);
            return null;
        }
    }

    function normalizeValidPromoCode(data, code) {
        const normalized = String(code || '').trim().toUpperCase();
        return data?.promos?.[normalized] ? normalized : null;
    }

    // --- Catalog accessors ---

    function getCatalog(data) {
        return data?.catalog || { verticals: [] };
    }

    function getAllCategories(data) {
        const cats = [];
        for (const v of getCatalog(data).verticals || []) {
            for (const c of v.categories || []) cats.push(c);
        }
        return cats;
    }

    function findCategoryByHandle(data, handle) {
        if (!handle) return null;
        return getAllCategories(data).find((c) => c.handle === handle) || null;
    }

    function getProductsForCategory(category) {
        return Array.isArray(category?.products) ? category.products : [];
    }

    function findProductByHandle(category, productHandle) {
        return getProductsForCategory(category).find((p) => p.handle === productHandle) || null;
    }

    function getAllCadences(product) {
        const cadences = [];
        for (const offer of product?.offers || []) {
            for (const c of offer.cadences || []) cadences.push(c);
        }
        return cadences;
    }

    function findCadenceById(product, cadenceId) {
        if (cadenceId == null) return null;
        return getAllCadences(product).find((c) => String(c.id) === String(cadenceId)) || null;
    }

    function getDefaultCadence(product) {
        for (const offer of product?.offers || []) {
            for (const c of offer.cadences || []) {
                if (c.supplyDays === 90) return c;
            }
        }
        return product?.offers?.[0]?.cadences?.[0] || null;
    }

    // --- Catalog index ---

    function buildCatalogIndex(data) {
        const catalog = getCatalog(data);

        const byMedId = {};
        const byCcProductId = {};
        const byHandle = {};
        const byVariantId = {};
        const byGroupKey = {};

        for (const vertical of catalog.verticals || []) {
            for (const category of vertical.categories || []) {
                const groupKey = category.handle || null;

                if (groupKey && !byGroupKey[groupKey]) {
                    byGroupKey[groupKey] = [];
                }

                for (const product of category.products || []) {
                    const productHandle = product.handle || null;

                    if (groupKey) {
                        byGroupKey[groupKey].push(product);
                    }

                    if (productHandle) {
                        byHandle[productHandle] = { groupKey, productHandle };
                    }

                    for (const offer of product.offers || []) {
                        for (const cadence of offer.cadences || []) {
                            const cadenceId = cadence.id;
                            const medId = cadence.medId;

                            if (cadenceId != null) {
                                byVariantId[String(cadenceId)] = {
                                    groupKey,
                                    productHandle,
                                    variantId: cadenceId
                                };
                                byCcProductId[String(cadenceId)] = {
                                    groupKey,
                                    productHandle,
                                    variantId: cadenceId,
                                    matchType: 'cadence'
                                };
                            }

                            if (medId) {
                                byMedId[medId] = {
                                    groupKey,
                                    productHandle,
                                    variantId: cadenceId,
                                    matchType: 'cadence'
                                };
                            }
                        }
                    }
                }
            }
        }

        return {
            ...catalog,
            lookup: {
                byMedId,
                byCcProductId,
                byHandle,
                byVariantId,
                byGroupKey
            }
        };
    }

    // --- URL sync helpers ---

    function syncMedToUrl(selection) {
        if (!selection?.medId) return;

        const url = new URL(window.location.href);
        url.searchParams.set('med', selection.medId);
        window.history.replaceState({}, '', url);
    }

    function syncCouponCodeToUrl(promoCode) {
        const url = new URL(window.location.href);
        if (promoCode) {
            url.searchParams.set('couponCode', promoCode);
        } else {
            url.searchParams.delete('couponCode');
        }
        window.history.replaceState({}, '', url);
    }

    function appendCouponCode(url, promoCode) {
        if (!url || !promoCode) return url;
        try {
            const next = new URL(url);
            next.searchParams.set('couponCode', promoCode);
            return next.toString();
        } catch {
            return url;
        }
    }

    function buildPatchedCheckoutUrl(checkoutUrl, ccProductId, quantity = 1) {
        if (!checkoutUrl || !ccProductId) return null;

        try {
            const parsed = new URL(checkoutUrl);
            const baseProductId = String(ccProductId).split(':')[0];
            parsed.searchParams.set('products', `${baseProductId}:${quantity}`);
            return parsed.toString();
        } catch (error) {
            console.warn('[BRXSession] Failed to patch checkout URL:', error);
            return null;
        }
    }

    function parseMedParam(search = window.location.search) {
        const params = new URLSearchParams(search);
        const med = params.get('med');

        if (!med) return null;

        const trimmed = med.trim();
        return trimmed || null;
    }

    function parseCouponCodeParam(search = window.location.search) {
        const params = new URLSearchParams(search);
        const code = params.get('couponCode');
        if (!code) return null;
        const trimmed = code.trim().toUpperCase();
        return trimmed || null;
    }

    function parseRecoveryParams(search = window.location.search) {
        const params = new URLSearchParams(search);
        const token     = params.get('token');
        const entryId   = params.get('entry_id');
        const expiresAt = params.get('expires_at');
        if (!token || !entryId || !expiresAt) return null;
        return { token, entryId, expiresAt };
    }

    function parseShortRecoveryParam(search = window.location.search) {
        const r = new URLSearchParams(search).get('r');
        return r ? r.trim() : null;
    }

    // --- Attribution Tracking Foundation ---

    const VISIT_ID_KEY = 'brx-visit-id';
    const TOUCHPOINT_SIGNATURE_KEY = 'brx-touchpoint-signature';
    const ATTRIBUTION_PARAM_KEYS = [
        'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
        'gclid', 'fbclid', '_ef_transaction_id', 'publisher_id'
    ];

    let inMemoryVisitId = null;

    function generateVisitId() {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        return 'visit-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
    }

    /**
     * Mints a visit_id the first time it's called with nothing in sessionStorage,
     * and reuses it for the rest of the tab's lifetime. Never localStorage —
     * must not survive tab close or persist across visits.
     */
    function getOrCreateVisitId() {
        try {
            const existing = window.sessionStorage.getItem(VISIT_ID_KEY);
            if (existing) return existing;

            const generated = generateVisitId();
            window.sessionStorage.setItem(VISIT_ID_KEY, generated);
            return generated;
        } catch (error) {
            console.warn('[BRXSession] sessionStorage unavailable; visit_id will not persist.', error);
            return inMemoryVisitId || (inMemoryVisitId = generateVisitId());
        }
    }

    /** Reads click-id/UTM fields fresh from the given search string. Only present, non-empty values are included. */
    function parseAttributionParams(search = window.location.search) {
        const params = new URLSearchParams(search);
        const result = {};
        for (const key of ATTRIBUTION_PARAM_KEYS) {
            const value = params.get(key);
            if (value) result[key] = value.trim();
        }
        return result;
    }

    /**
     * Builds the full attribution payload for any call that accepts it: UTM/
     * click-id params + referrer + landing_page_url, all read fresh at call
     * time (never cached from first load), plus visit_id.
     */
    function buildAttributionFields() {
        return {
            ...parseAttributionParams(),
            ...(document.referrer && { referrer: document.referrer }),
            landing_page_url: window.location.href,
            visit_id: getOrCreateVisitId()
        };
    }

    /**
     * Fire-and-forget POST /attribution/touchpoints for a session we already
     * hold. No-ops unless: brxSessionId is present (first touch for a brand-
     * new visitor is handled by the minting endpoints, not this one), the
     * current URL carries at least one click id / UTM param, and this exact
     * signature hasn't already been sent this visit (sessionStorage-scoped —
     * a courtesy dedup only, the backend's own dedup is authoritative).
     * Pass { keepalive: true } when calling immediately before a synchronous
     * navigation so the request survives the page unload.
     */
    function maybeSendTouchpoint(brxSessionId, apiBase, options = {}) {
        if (!brxSessionId || !apiBase) return;

        const clickAndUtmParams = parseAttributionParams();
        if (Object.keys(clickAndUtmParams).length === 0) return;

        const signature = JSON.stringify(clickAndUtmParams);
        try {
            if (window.sessionStorage.getItem(TOUCHPOINT_SIGNATURE_KEY) === signature) return;
        } catch (error) {
            // sessionStorage unavailable — fall through and send anyway.
        }

        const payload = { brx_session_id: brxSessionId, ...buildAttributionFields() };

        fetch(`${apiBase}/attribution/touchpoints`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: !!options.keepalive
        })
            .then(() => {
                try { window.sessionStorage.setItem(TOUCHPOINT_SIGNATURE_KEY, signature); } catch (error) {}
            })
            .catch(() => {});
    }

    // Returns true if a redirect was triggered (caller should bail out of init).
    async function resolveShortRecoveryCode(data, sessionId) {
        try {
            const apiBase = data?.settings?.apiBaseUrl;
            if (!apiBase) return false;

            const resp = await fetch(`${apiBase}/session/${encodeURIComponent(sessionId)}/recover`);
            if (!resp.ok) return false;

            const json = await resp.json();
            const recoveryLink = json?.data?.recovery_link;
            if (!recoveryLink) return false
            let linkUrl;
            try { linkUrl = new URL(recoveryLink); } catch { return false; }

            const token     = linkUrl.searchParams.get('token');
            const entryId   = linkUrl.searchParams.get('entry_id');
            const expiresAt = linkUrl.searchParams.get('expires_at');

            if (token && entryId && expiresAt) {
                // Intake path: rewrite the current URL with Embeddables params so all
                // downstream scripts (Embeddables loader, parseRecoveryParams) see them normally.
                const next = new URL(window.location.href);
                next.searchParams.delete('r');
                next.searchParams.set('token', token);
                next.searchParams.set('entry_id', entryId);
                next.searchParams.set('expires_at', expiresAt);
                window.history.replaceState({}, '', next.toString());
                return false;
            }

            // Checkout path: store the recovery URL in session and redirect.
            const checkoutRecoveryUrl = data?.settings?.checkoutRecoveryUrl;
            if (!checkoutRecoveryUrl) return false;

            const raw = window.localStorage.getItem(STORAGE_KEY);
            const existing = safeJsonParse(raw, {});
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
                version: SESSION_VERSION,
                selection: existing.selection || null,
                checkout: {
                    ...(existing.checkout || {}),
                    isComplete: true,
                    checkoutUrl: recoveryLink,
                },
                promoCode: existing.promoCode || null,
                brxFlowPage: existing.brxFlowPage || 'welcome',
                brxSessionId: existing.brxSessionId || null,
                updatedAt: Date.now(),
            }));

            // Fire before navigating away — keepalive so the request survives
            // the synchronous redirect immediately below.
            maybeSendTouchpoint(existing.brxSessionId || null, apiBase, { keepalive: true });

            window.location.assign(checkoutRecoveryUrl);
            return true;
        } catch (err) {
            console.warn('[BRXSession] Failed to resolve short recovery code.', err);
            return false;
        }
    }

    // --- Selection resolution ---

    function findSelectionByMedId(indexedCatalog, medId) {
        if (!medId) return null;

        const match = indexedCatalog?.lookup?.byMedId?.[medId];
        if (!match) return null;

        return {
            medId,
            groupKey: match.groupKey,
            productHandle: match.productHandle,
            variantId: match.variantId,
            source: `med_lookup_${match.matchType || 'unknown'}`
        };
    }

    function findSelectionByCcProductId(indexedCatalog, ccProductId) {
        if (!ccProductId) return null;

        const match = indexedCatalog?.lookup?.byCcProductId?.[ccProductId];
        if (!match) return null;

        return {
            ccProductId,
            groupKey: match.groupKey,
            productHandle: match.productHandle,
            variantId: match.variantId,
            source: `cc_lookup_${match.matchType || 'unknown'}`
        };
    }

    function createEmptySelection() {
        return {
            medId: null,
            ccProductId: null,
            groupKey: null,
            productHandle: null,
            variantId: null,
            source: null
        };
    }

    function createEmptyCheckout() {
        return {
            isComplete: false,
            checkoutUrl: null,
            providerSessionId: null,
            medId: null,
            ccProductId: null,
            groupKey: null,
            productHandle: null,
            variantId: null,
            completedAt: null
        };
    }

    function createCheckoutScaffoldFromSelection(selection) {
        return {
            isComplete: false,
            checkoutUrl: null,
            providerSessionId: null,
            medId: selection.medId || null,
            ccProductId: selection.ccProductId || null,
            groupKey: selection.groupKey || null,
            productHandle: selection.productHandle || null,
            variantId: selection.variantId || null,
            completedAt: null
        };
    }

    function normalizeSelection(data, selection) {
        if (!selection) return createEmptySelection();

        const category = findCategoryByHandle(data, selection.groupKey);
        if (!category) return createEmptySelection();

        const product = findProductByHandle(category, selection.productHandle);
        if (!product) return createEmptySelection();

        let cadence = findCadenceById(product, selection.variantId);

        if (!cadence && selection.ccProductId) {
            cadence = getAllCadences(product).find(
                (c) => String(c.id) === String(selection.ccProductId)
            ) || null;
        }

        const resolved = cadence || getDefaultCadence(product);

        return {
            medId: selection.medId || resolved?.medId || null,
            ccProductId: resolved?.id != null ? String(resolved.id) : (selection.ccProductId || null),
            groupKey: category.handle,
            productHandle: product.handle,
            variantId: resolved?.id ?? null,
            source: selection.source || 'normalized'
        };
    }

    // --- Persistence ---

    function getPersistableSession(sessionState) {
        return {
            version: SESSION_VERSION,
            selection: {
                medId: sessionState.selection?.medId || null,
                ccProductId: sessionState.selection?.ccProductId || null,
                groupKey: sessionState.selection?.groupKey || null,
                productHandle: sessionState.selection?.productHandle || null,
                variantId: sessionState.selection?.variantId || null,
                source: sessionState.selection?.source || 'persisted'
            },
            checkout: {
                isComplete: sessionState.checkout?.isComplete || false,
                checkoutUrl: sessionState.checkout?.checkoutUrl || null,
                providerSessionId: sessionState.checkout?.providerSessionId || null,
                medId: sessionState.checkout?.medId || null,
                ccProductId: sessionState.checkout?.ccProductId || null,
                groupKey: sessionState.checkout?.groupKey || null,
                productHandle: sessionState.checkout?.productHandle || null,
                variantId: sessionState.checkout?.variantId || null,
                completedAt: sessionState.checkout?.completedAt || null
            },
            promoCode: sessionState.promoCode || null,
            brxFlowPage: sessionState.brxFlowPage || 'welcome',
            brxSessionId: sessionState.brxSessionId || null,
            updatedAt: Date.now()
        };
    }

    function loadStoredSession() {
        try {
            const raw = window.localStorage.getItem(STORAGE_KEY);
            const parsed = safeJsonParse(raw, null);

            if (!parsed || typeof parsed !== 'object') return null;

            if (parsed.version === 2) {
                parsed.brxFlowPage = parsed.hasAnsweredWelcome ? 'savvy' : 'welcome';
                delete parsed.hasAnsweredWelcome;
                parsed.version = SESSION_VERSION;
            }

            if (parsed.version !== SESSION_VERSION) return null;

            return parsed;
        } catch (error) {
            console.warn('[BRXSession] Failed to load session from localStorage.', error);
            return null;
        }
    }

    function saveStoredSession(sessionState) {
        try {
            const payload = getPersistableSession(sessionState);
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch (error) {
            console.warn('[BRXSession] Failed to save session to localStorage.', error);
        }
    }

    function resolveInitialSelection(data, indexedCatalog) {
        const storedSession = loadStoredSession();
        const storedCheckout = storedSession?.checkout || null;
        const storedSelection = storedSession?.selection || null;

        const recoveryParams = parseRecoveryParams();
        const isRecoverySession = recoveryParams !== null;

        const medParam = parseMedParam();

        const urlCouponCode = parseCouponCodeParam();
        const storedPromoCode = storedSession?.promoCode || null;
        const resolvedPromoCode = normalizeValidPromoCode(data, urlCouponCode)
            || normalizeValidPromoCode(data, storedPromoCode);

        const storedFlowPage = storedSession?.brxFlowPage || 'welcome';
        const brxFlowPage = isRecoverySession ? 'savvy' : storedFlowPage;
        const brxSessionId = storedSession?.brxSessionId || null;

        if (medParam) {
            const urlSelection = findSelectionByMedId(indexedCatalog, medParam);

            if (urlSelection) {
                return {
                    selection: normalizeSelection(data, {
                        ...urlSelection,
                        source: 'url'
                    }),
                    checkout: storedCheckout,
                    promoCode: resolvedPromoCode,
                    brxFlowPage,
                    brxSessionId,
                    isRecoverySession,
                    resolutionSource: 'url'
                };
            }
        }

        if (storedSelection) {
            const normalizedStoredSelection = normalizeSelection(data, storedSelection);

            if (normalizedStoredSelection.productHandle) {
                return {
                    selection: {
                        ...normalizedStoredSelection,
                        source: 'storage'
                    },
                    checkout: storedCheckout,
                    promoCode: resolvedPromoCode,
                    brxFlowPage,
                    brxSessionId,
                    isRecoverySession,
                    resolutionSource: 'storage'
                };
            }
        }

        const configuredDefault = data?.defaultSelection;
        if (configuredDefault?.productHandle) {
            const defaultSel = normalizeSelection(data, {
                ...configuredDefault,
                source: 'configured_default'
            });
            if (defaultSel.productHandle) {
                return {
                    selection: defaultSel,
                    checkout: storedCheckout,
                    promoCode: resolvedPromoCode,
                    brxFlowPage,
                    brxSessionId,
                    isRecoverySession,
                    resolutionSource: 'configured_default'
                };
            }
        }

        const firstCategory = getAllCategories(data)[0] || null;
        const firstProduct = firstCategory ? getProductsForCategory(firstCategory)[0] : null;

        if (firstProduct) {
            const firstCadence = getDefaultCadence(firstProduct);

            const defaultSelection = normalizeSelection(data, {
                medId: firstCadence?.medId || null,
                ccProductId: firstCadence?.id != null ? String(firstCadence.id) : null,
                groupKey: firstCategory.handle,
                productHandle: firstProduct.handle,
                variantId: firstCadence?.id ?? null,
                source: 'default'
            });

            if (defaultSelection.productHandle) {
                return {
                    selection: defaultSelection,
                    checkout: storedCheckout,
                    promoCode: resolvedPromoCode,
                    brxFlowPage,
                    brxSessionId,
                    isRecoverySession,
                    resolutionSource: 'default'
                };
            }
        }

        return {
            selection: createEmptySelection(),
            checkout: storedCheckout,
            promoCode: resolvedPromoCode,
            brxFlowPage,
            brxSessionId,
            isRecoverySession,
            resolutionSource: 'empty'
        };
    }

    // --- Store ---

    function createSessionStore(data, indexedCatalog) {
        let state = {
            selection: createEmptySelection(),
            checkout: createEmptyCheckout(),
            promoCode: null,
            brxFlowPage: 'welcome',
            brxSessionId: null,
            isRecoverySession: false,
            resolutionSource: 'empty',
            initialized: false
        };

        const subscribers = new Set();

        function emit() {
            const snapshot = getState();
            subscribers.forEach((callback) => {
                try {
                    callback(snapshot);
                } catch (error) {
                    console.error('[BRXSession] Subscriber error.', error);
                }
            });
        }

        // --- Public API ---

        /** Initialize the store from bootstrap data, stored session, and URL params. */
        function init() {
            const resolved = resolveInitialSelection(data, indexedCatalog);

            let initialCheckout;

            if (resolved.checkout) {
                initialCheckout = resolved.checkout;
            } else if (resolved.selection.productHandle) {
                initialCheckout = createCheckoutScaffoldFromSelection(resolved.selection);
            } else {
                initialCheckout = createEmptyCheckout();
            }

            state = {
                selection: resolved.selection,
                checkout: initialCheckout,
                promoCode: resolved.promoCode || null,
                brxFlowPage: resolved.brxFlowPage || 'welcome',
                brxSessionId: resolved.brxSessionId || null,
                isRecoverySession: resolved.isRecoverySession || false,
                resolutionSource: resolved.resolutionSource,
                initialized: true
            };

            if (state.selection.productHandle) {
                saveStoredSession(state);
            }

            emit();
        }

        /** Returns a snapshot of the current session state. */
        function getState() {
            const isStale =
                state.checkout.isComplete &&
                state.selection.ccProductId !== state.checkout.ccProductId;

            return {
                selection: { ...state.selection },
                checkout: { ...state.checkout, isStale },
                promoCode: state.promoCode,
                brxFlowPage: state.brxFlowPage,
                isRecoverySession: state.isRecoverySession,
                resolutionSource: state.resolutionSource,
                initialized: state.initialized
            };
        }

        /** Returns the stored checkout context (provider session, completion fields). */
        function getCheckoutContext() {
            return { ...state.checkout };
        }

        /** Returns the best available outbound checkout URL for the current session. */
        function getResolvedCheckoutUrl() {
            const checkout = state.checkout;
            const selection = state.selection;

            if (!checkout?.isComplete || !checkout?.checkoutUrl) {
                return { status: 'incomplete', url: null, reason: 'No completed checkout exists.' };
            }

            const isStale = selection.ccProductId !== checkout.ccProductId;

            if (!isStale) {
                return { status: 'valid', url: appendCouponCode(checkout.checkoutUrl, state.promoCode), reason: null };
            }

            if (!selection?.ccProductId) {
                return { status: 'unpatchable', url: null, reason: 'Missing current selection ccProductId.' };
            }

            const patchedUrl = buildPatchedCheckoutUrl(checkout.checkoutUrl, selection.ccProductId);

            if (!patchedUrl) {
                return { status: 'unpatchable', url: null, reason: 'Unable to patch checkout URL.' };
            }

            return { status: 'patched', url: appendCouponCode(patchedUrl, state.promoCode), reason: null };
        }

        /**
         * Subscribe to state changes. Callback receives a state snapshot on every update.
         * Also delivers the current state immediately upon subscription.
         * Returns an unsubscribe function.
         */
        function subscribe(callback) {
            if (typeof callback !== 'function') {
                throw new Error('[BRXSession] subscribe requires a function.');
            }

            subscribers.add(callback);

            try {
                callback(getState());
            } catch (error) {
                console.error('[BRXSession] Subscriber error on initial delivery.', error);
            }

            return function unsubscribe() {
                subscribers.delete(callback);
            };
        }

        /**
         * Persist and normalize the active product selection.
         * Source should identify the caller, e.g. 'drawer', 'pdp', 'url', 'default'.
         */
        function setSelection(nextSelection, source = 'manual') {
            const normalizedSelection = normalizeSelection(data, {
                ...nextSelection,
                source
            });

            state = {
                ...state,
                selection: normalizedSelection,
                resolutionSource: source,
                initialized: true
            };

            if (!state.checkout.isComplete) {
                state = {
                    ...state,
                    checkout: normalizedSelection.productHandle
                        ? createCheckoutScaffoldFromSelection(normalizedSelection)
                        : createEmptyCheckout()
                };
            }

            // Always persist (rather than clearStoredSession() on the empty-selection
            // path) so a captured brxSessionId survives an empty selection — see
            // clearSelection() below for the same fix.
            saveStoredSession(state);

            syncMedToUrl(normalizedSelection);
            emit();

            if (state.brxSessionId && apiBaseUrl && normalizedSelection.productHandle) {
                fetch(`${apiBaseUrl}/session/${encodeURIComponent(state.brxSessionId)}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        selection: {
                            product_handle: normalizedSelection.productHandle,
                            variant_id: normalizedSelection.variantId != null ? String(normalizedSelection.variantId) : null,
                        },
                    }),
                }).catch(() => {});
            }
        }

        /** Resolve and set selection by med ID. Returns false if the med ID is not found. */
        function setSelectionFromMedId(medId, source = 'manual_med') {
            const matchedSelection = findSelectionByMedId(indexedCatalog, medId);

            if (!matchedSelection) {
                console.warn('[BRXSession] No selection found for med ID:', medId);
                return false;
            }

            setSelection(matchedSelection, source);
            return true;
        }

        /** Clear the active selection and any associated checkout scaffold. */
        function clearSelection() {
            state = {
                ...state,
                selection: createEmptySelection(),
                checkout: createEmptyCheckout(),
                resolutionSource: 'cleared',
                initialized: true
            };

            // Persist (rather than clearStoredSession()'s full localStorage.removeItem)
            // so a captured brxSessionId survives clearing the selection.
            saveStoredSession(state);
            emit();
        }

        /** Record a completed provider checkout session against the current selection. */
        function setCheckoutContext(completionFields) {
            state = {
                ...state,
                brxSessionId: completionFields.brxSessionId || state.brxSessionId || null,
                checkout: {
                    ...state.checkout,
                    isComplete: true,
                    checkoutUrl: completionFields.checkoutUrl || null,
                    providerSessionId: completionFields.providerSessionId || null,
                    ccProductId: completionFields.ccProductId || state.checkout.ccProductId || null,
                    completedAt: completionFields.completedAt || Date.now()
                }
            };

            saveStoredSession(state);
            emit();
        }

        /** Clear the completed checkout context, reverting to a scaffold from the current selection. */
        function clearCheckoutContext() {
            state = {
                ...state,
                checkout: state.selection.productHandle
                    ? createCheckoutScaffoldFromSelection(state.selection)
                    : createEmptyCheckout()
            };

            saveStoredSession(state);
            emit();
        }

        /**
         * Set the active promo code. Validates against configured promos before persisting.
         * Invalid codes are silently rejected.
         */
        function setPromoCode(code) {
            const normalized = normalizeValidPromoCode(data, code);
            state = { ...state, promoCode: normalized };
            saveStoredSession(state);
            syncCouponCodeToUrl(normalized);
            emit();

            if (state.brxSessionId && apiBaseUrl) {
                fetch(`${apiBaseUrl}/session/${encodeURIComponent(state.brxSessionId)}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ promo_code: normalized }),
                }).catch(() => {});
            }
        }

        /** Clear the active promo code. */
        function clearPromoCode() {
            state = { ...state, promoCode: null };
            saveStoredSession(state);
            syncCouponCodeToUrl(null);
            emit();

            if (state.brxSessionId && apiBaseUrl) {
                fetch(`${apiBaseUrl}/session/${encodeURIComponent(state.brxSessionId)}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ promo_code: null }),
                }).catch(() => {});
            }
        }

        /** Returns the current promo code string, or null if none is set. */
        function getPromoCode() {
            return state.promoCode;
        }

        /** Returns the current brx_session_id, or null if none has been captured yet. */
        function getBrxSessionId() {
            return state.brxSessionId;
        }

        /**
         * Records a brx_session_id learned from an external call (e.g. footer-form's
         * or popup-form-ac's POST /forms/{id}/submit response). Fills in only if we
         * don't already have one — once captured, a session id should never be
         * silently swapped out from under an in-progress journey by a later,
         * lower-authority call. (setCheckoutContext is the higher-authority
         * post-checkout path and keeps its existing overwrite behavior.)
         */
        function captureBrxSessionId(id) {
            const trimmed = String(id || '').trim();
            if (!trimmed || state.brxSessionId) return false;

            state = { ...state, brxSessionId: trimmed };
            saveStoredSession(state);
            emit();
            return true;
        }

        /** Advance or set the BRX wrapper flow page. page: 'welcome' | 'savvy' | 'checkout_ready' */
        function setBrxFlowPage(page) {
            state = { ...state, brxFlowPage: page };
            saveStoredSession(state);
            emit();
        }

        /** Returns the current BRX wrapper flow page string. */
        function getBrxFlowPage() {
            return state.brxFlowPage;
        }

        /**
         * Resolve a selection object from a Commerce Cloud product ID.
         * Returns null if not found.
         */
        function findSelectionByCcProductIdPublic(ccProductId) {
            return findSelectionByCcProductId(indexedCatalog, ccProductId);
        }

        /** Returns the full catalog object from the bootstrap payload. */
        function getCatalogPublic() {
            return getCatalog(data);
        }

        /** Returns the active promos map { [CODE]: { type, value, label } }. Empty object before catalog resolves. */
        function getPromos() {
            return data.promos || {};
        }

        /**
         * Returns the product object for the given handle, or null if not found.
         * Walks the catalog index to locate the product across all groups.
         */
        function getProductByHandle(handle) {
            const entry = indexedCatalog?.lookup?.byHandle?.[handle];
            if (!entry) return null;
            const category = findCategoryByHandle(data, entry.groupKey);
            if (!category) return null;
            return findProductByHandle(category, handle);
        }

        /** Returns the full product object for the current selection, or null. */
        function getSelectionProduct() {
            const { groupKey, productHandle } = state.selection;
            if (!groupKey || !productHandle) return null;
            const category = findCategoryByHandle(data, groupKey);
            if (!category) return null;
            return findProductByHandle(category, productHandle);
        }

        /** Returns the cadence object for the current selection, or null. */
        function getSelectionVariant() {
            const product = getSelectionProduct();
            if (!product) return null;
            return findCadenceById(product, state.selection.variantId);
        }

        /**
         * Returns the checkout return state for the current session.
         * mode: 'ready'  — valid or patched checkout URL exists; skip intake
         * mode: 'stale'  — checkout is complete but URL cannot be resolved
         * mode: 'none'   — no completed checkout; run normal intake
         */
        function getCheckoutReturnState() {
            const snapshot = getState();
            const checkout = snapshot.checkout || {};
            const resolved = getResolvedCheckoutUrl();

            if (!checkout.isComplete) {
                return { mode: 'none', url: null, status: 'incomplete', selection: snapshot.selection, checkout };
            }

            if (resolved.status === 'valid' || resolved.status === 'patched') {
                return { mode: 'ready', url: resolved.url, status: resolved.status, selection: snapshot.selection, checkout };
            }

            if (resolved.status === 'unpatchable') {
                return { mode: 'stale', url: null, status: resolved.status, selection: snapshot.selection, checkout };
            }

            return { mode: 'none', url: null, status: resolved.status || 'incomplete', selection: snapshot.selection, checkout };
        }

        return {
            init,
            getState,
            getCheckoutContext,
            getResolvedCheckoutUrl,
            subscribe,
            setSelection,
            setSelectionFromMedId,
            clearSelection,
            setCheckoutContext,
            clearCheckoutContext,
            findSelectionByCcProductId: findSelectionByCcProductIdPublic,
            setPromoCode,
            clearPromoCode,
            getPromoCode,
            setBrxFlowPage,
            getBrxFlowPage,
            getCatalog: getCatalogPublic,
            getPromos,
            getProductByHandle,
            getSelectionProduct,
            getSelectionVariant,
            getCheckoutReturnState,
            getBrxSessionId,
            captureBrxSessionId,
            getVisitId: getOrCreateVisitId,
            getAttributionFields: buildAttributionFields
        };
    }

    const config = getUiConfig();

    if (!config) return;

    const apiBaseUrl = config?.settings?.apiBaseUrl;
    const catalogData = apiBaseUrl ? await fetchCatalogData(apiBaseUrl) : null;

    const data = {
        ...config,
        catalog: catalogData?.catalog || { verticals: [] },
        promos: catalogData?.promos || {},
    };

    // Merge default product from the header section's own JSON element.
    // The header section has direct access to section.settings, unlike snippets
    // that would need to look up the section by ID across section groups.
    const headerDefaultsEl = document.getElementById('brx-header-defaults');
    if (headerDefaultsEl) {
        const headerDefaults = safeJsonParse(headerDefaultsEl.textContent);
        if (headerDefaults?.defaultSelection?.productHandle) {
            data.defaultSelection = headerDefaults.defaultSelection;
        }
    }

    const indexedCatalog = buildCatalogIndex(data);
    const shortCode = parseShortRecoveryParam();
    if (shortCode) {
        const redirected = await resolveShortRecoveryCode(data, shortCode);
        if (redirected) return;
    }

    const store = createSessionStore(data, indexedCatalog);
    store.init();

    window.BRXSession = store;
    window.dispatchEvent(new CustomEvent('brx:session-ready'));

    // Attribution Tracking Foundation: fire-and-forget touchpoint for a
    // returning visitor (existing brxSessionId) whose pageview carries fresh
    // UTM/click-id signal — see maybeSendTouchpoint's gating above.
    maybeSendTouchpoint(store.getBrxSessionId(), apiBaseUrl);

    // --- Global event listeners ---

    window.addEventListener('brx:set-checkout-context', async (event) => {
        try {
            const detail = event?.detail || {};

            store.setCheckoutContext(detail);

            const brxSessionId = detail.brxSessionId;
            const dosableSessionId = detail.providerSessionId;
            const ccProductId = detail.ccProductId ? String(detail.ccProductId) : null;

            if (apiBaseUrl && brxSessionId && ccProductId) {
                const emailAddress = detail.email || null;
                const firstName = detail.first_name || null;
                const lastName = detail.last_name || null;
                const phoneNumber = detail.phone ? String(detail.phone) : null;
                const smsOptIn = typeof detail.sms_opt_in === 'boolean' ? detail.sms_opt_in : null;
                const selection = store.getState().selection;

                const res = await fetch(`${apiBaseUrl}/checkout/session`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        brx_session_id: brxSessionId,
                        dosable_session_id: dosableSessionId || '',
                        cc_product_id: ccProductId,
                        product_handle: selection.productHandle || null,
                        variant_id: selection.variantId ? String(selection.variantId) : null,
                        email: emailAddress,
                        first_name: firstName,
                        last_name: lastName,
                        phone: phoneNumber,
                        ...(smsOptIn !== null && { sms_opt_in: smsOptIn }),
                        ...(store.getPromoCode() && { promo_code: store.getPromoCode() }),
                        ...buildAttributionFields(),
                    }),
                });
                const json = await res.json();
                const brxCheckoutUrl = json?.data?.checkout_url;
                if (brxCheckoutUrl) {
                    store.setCheckoutContext({ ...detail, checkoutUrl: brxCheckoutUrl });
                } else {
                    console.warn('[BRXSession] POST /checkout/session returned no checkout_url.', json);
                }
            }
        } catch (error) {
            console.warn('[BRXSession] Failed to apply checkout context:', error);
        } finally {
            // Signal the return surface to take over regardless of BRX URL outcome.
            // Dosable URL from the initial setCheckoutContext is always the fallback.
            store.setBrxFlowPage('checkout_ready');
            window.dispatchEvent(new CustomEvent('brx:checkout-ready'));
        }
    });
})();
